import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { ArrowRight, TrendingUp, Shield, Zap } from 'lucide-react';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden grain-overlay">
      {/* Background Image with Overlay */}
      <div
        style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 0,
          backgroundImage: 'url(https://images.unsplash.com/photo-1677442136019-21780ecad995?q=80&w=2000)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.5), rgba(0,0,0,0.4), rgba(0,0,0,0.75))' }}></div>
      </div>

      {/* Animated gradient orbs */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-[#FF6600] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#FF8C42] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" style={{ animationDelay: '2s' }}></div>

      {/* Content */}
      <div className="relative z-20 container mx-auto px-6 py-32">
        <div className="max-w-5xl mx-auto text-center">
          {/* Badge */}
          <div
            className={`inline-flex items-center space-x-2 bg-[#FF6600]/10 border border-[#FF6600]/30 rounded-full px-6 py-2 mb-8 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <Zap className="w-4 h-4 text-[#FF6600]" />
            <span className="text-[#FF6600] font-semibold text-sm">Enterprise AI Solutions</span>
          </div>

          {/* Main Heading */}
          <h1
            className={`text-5xl md:text-7xl lg:text-8xl font-bold mb-6 leading-tight transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Transform Your Workforce with{' '}
            <span className="text-gradient-orange">AI Intelligence</span>
          </h1>

          {/* Subheading */}
          <p
            className={`text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Drive 70% growth with OpenClaw AI Agents. Keep your data secure with private LLMs running locally on enterprise infrastructure.
          </p>

          {/* Stats */}
          <div
            className={`flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-12 mb-12 transition-all duration-700 delay-300 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-[#FF6600]/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-[#FF6600]" />
              </div>
              <div className="text-left">
                <div className="text-3xl font-bold text-white">70%</div>
                <div className="text-sm text-gray-400">Growth Focus</div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-[#FF6600]/20 rounded-lg flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#FF6600]" />
              </div>
              <div className="text-left">
                <div className="text-3xl font-bold text-white">100%</div>
                <div className="text-sm text-gray-400">Data Security</div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-[#FF6600]/20 rounded-lg flex items-center justify-center">
                <Zap className="w-6 h-6 text-[#FF6600]" />
              </div>
              <div className="text-left">
                <div className="text-3xl font-bold text-white">Local</div>
                <div className="text-sm text-gray-400">LLM Processing</div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div
            className={`flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 transition-all duration-700 delay-400 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <Button
              onClick={scrollToContact}
              className="group bg-[#FF6600] hover:bg-[#FF8C42] text-white font-bold px-8 py-6 text-lg rounded-xl transition-all duration-300 shadow-2xl hover:shadow-[#FF6600]/50 animate-glow"
            >
              Book a Call
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
            </Button>
            <Button
              onClick={() => document.getElementById('agents').scrollIntoView({ behavior: 'smooth' })}
              variant="outline"
              className="border-2 border-white/20 bg-white/5 backdrop-blur-sm hover:bg-white/10 text-white font-semibold px-8 py-6 text-lg rounded-xl transition-all duration-300"
            >
              Explore AI Agents
            </Button>
          </div>

          {/* Trust Badge */}
          <p
            className={`mt-12 text-sm text-gray-400 transition-all duration-700 delay-500 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Powered by Nvidia DGX Spark & Mac Studio Infrastructure
          </p>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-[#FF6600] rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
